import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Card from '@mui/material/Card'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import OutlinedInput from '@mui/material/OutlinedInput'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import LogoutIcon from '@mui/icons-material/Logout'
import SnackAlert from '@/components/snack-alert'
import useLogout from '@/hooks/useLogout'

import LayoutContent from '@/components/LayoutContent'
import {
  useChangePasswordMutation,
  useEditUserMutation
} from '../users/users-api'
import { useGetDetailsQuery } from '@/api'
import { MIN_PASSWORD_LENGTH } from '../change-password'

const MyProfile = () => {
  const { t } = useTranslation()
  const logout = useLogout()
  const { data: user, isSuccess, isLoading } = useGetDetailsQuery()

  const [editUser, { isSuccess: editSuccess, error }] = useEditUserMutation()
  const [
    changePassword,
    {
      isSuccess: changeSuccess,
      isLoading: changeLoading,
      error: changeErrorData
    }
  ] = useChangePasswordMutation()
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [passwordSnackBar, setPasswordSnackbar] = useState(false)
  const [tab, setTab] = useState(0)
  const [formPassword, setFormPassword] = useState({
    oldPassword: '',
    newPassword: '',
    newPasswordRetry: ''
  })
  const [form, setForm] = useState({
    login: '',
    password: '',
    iin: '',
    name: '',
    lastName: '',
    phone: '',
    email: '',
    partnerId: null
  })

  const handlePasswordChange = ({ target }) => {
    setFormPassword({
      ...formPassword,
      [target.name]: target.value
    })
  }

  const handlePasswordClear = () => {
    setFormPassword({
      oldPassword: '',
      newPassword: '',
      newPasswordRetry: ''
    })
  }

  const handleChange = ({ target }) => {
    setForm({
      ...form,
      [target.name]: target.value
    })
  }

  const handleSnackOpen = () => {
    setOpenSnackbar(true)
  }

  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }

    setOpenSnackbar(false)
  }

  const handleTab = (event, newValue) => {
    setTab(newValue)
  }

  const passwordOpenSnack = () => {
    setPasswordSnackbar(true)
  }

  const passwordCloseSnack = () => {
    setPasswordSnackbar(false)
  }

  const handleSubmitPassword = async () => {
    try {
      await changePassword({
        old_password: formPassword?.oldPassword,
        new_password: formPassword?.newPassword
      }).unwrap()
      handlePasswordClear()
    } catch (err) {
      console.log(err)
    }
    passwordOpenSnack()
  }

  const handleSubmit = async () => {
    try {
      await editUser({
        iin: form.iin,
        first_name: form.name,
        last_name: form.lastName,
        phone_number: form.phone,
        email: form.email
      })
      handleSnackOpen()
    } catch (err) {
      console.log(err)
    }
  }

  useEffect(() => {
    if (user) {
      setForm({
        id: user.id,
        login: user?.username,
        iin: user?.iin,
        name: user?.first_name,
        lastName: user?.last_name,
        phone: user?.phone_number,
        email: user?.email,
        partnerId: user?.partner_id
      })
    }
  }, [form.password, user])

  return (
    <LayoutContent title={t('myProfile')}>
      <Card>
        <Tabs value={tab} onChange={handleTab} aria-label="tabs">
          <Tab label={t('common_info')} />
          <Tab label={t('change_password')} />
        </Tabs>
      </Card>
      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <CircularProgress />
        </Box>
      )}
      {isSuccess && tab === 0 && (
        <Card sx={{ pr: 4, pl: 4, pt: 2, pb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              marginBottom: 3
            }}>
            {t('info')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <FormControl fullWidth sx={{ mr: 1 }}>
              <InputLabel id="login-label">{t('login')}</InputLabel>
              <OutlinedInput
                name="login"
                value={form.login}
                onChange={handleChange}
                label={t('login')}
              />
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('email')}</InputLabel>
              <OutlinedInput
                name="email"
                value={form.email}
                onChange={handleChange}
                label={t('email')}
              />
            </FormControl>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 2
            }}
          >
            <FormControl fullWidth sx={{ mr: 1 }}>
              <InputLabel>{t('name')}</InputLabel>
              <OutlinedInput
                name="name"
                value={form.name}
                onChange={handleChange}
                label={t('name')}
              />
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('last_name')}</InputLabel>
              <OutlinedInput
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                label={t('last_name')}
              />
            </FormControl>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 2
            }}
          >
            <FormControl fullWidth sx={{ mr: 1 }}>
              <InputLabel>{t('phone')}</InputLabel>
              <OutlinedInput
                name="phone"
                value={form.phone}
                onChange={handleChange}
                label={t('phone')}
              />
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>{t('iin')}</InputLabel>
              <OutlinedInput
                name="iin"
                value={form.iin}
                onChange={handleChange}
                label={t('iin')}
              />
            </FormControl>
          </Box>
          <Button
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={handleSubmit}
            sx={{
              mt: 5,
              display: 'flex'
            }}
          >
            {t('save')}
          </Button>
        </Card>
      )}
      {tab === 1 && (
        <Box>
          <Card sx={{ pl: 4, pr: 4, pt: 3 }}>
            <Alert severity="info" variant="outlined">
              <AlertTitle>{t('attention')}</AlertTitle>
              {t('for_security_reasons')}{' '}
              <b>{t('dont_show_your_password_to_anyone!')}</b>
            </Alert>
          </Card>
          <Card sx={{ pr: 4, pl: 4, pt: 2, pb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                marginBottom: 3
              }}>
              {t('change_password')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <FormControl fullWidth sx={{ mr: 1, width: '50%' }}>
                <InputLabel>{t('old_password')}</InputLabel>
                <OutlinedInput
                  name="oldPassword"
                  type="password"
                  value={formPassword.oldPassword || ''}
                  onChange={handlePasswordChange}
                  label={t('old_password')}
                />
              </FormControl>
            </Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}
            >
              <FormControl sx={{ mr: 1 }} fullWidth>
                <TextField
                  name="newPassword"
                  type="password"
                  value={formPassword.newPassword || ''}
                  onChange={handlePasswordChange}
                  label={t('new_password')}
                  helperText={
                    formPassword.newPassword.length &&
                    formPassword.newPassword.length < MIN_PASSWORD_LENGTH
                      ? t('password_must_be_a_minimum_of_8_characters')
                      : null
                  }
                  error={
                    formPassword.newPassword.length &&
                    formPassword.newPassword.length < MIN_PASSWORD_LENGTH
                  }
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  name="newPasswordRetry"
                  type="password"
                  value={formPassword.newPasswordRetry || ''}
                  onChange={handlePasswordChange}
                  label={t('repeat_the_new_password')}
                  helperText={
                    formPassword.newPasswordRetry.length &&
                    formPassword.newPasswordRetry !== formPassword.newPassword
                      ? t('the_password_must_match')
                      : null
                  }
                  error={
                    formPassword.newPasswordRetry.length &&
                    formPassword.newPasswordRetry !== formPassword.newPassword
                  }
                />
              </FormControl>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                mt: 5
              }}
            >
              <Button
                disabled={
                  !formPassword.newPassword.length ||
                  formPassword.newPassword !== formPassword.newPasswordRetry ||
                  !formPassword.oldPassword.length ||
                  formPassword.newPassword.length < MIN_PASSWORD_LENGTH
                }
                sx={{ mr: 1.5 }}
                variant="contained"
                loading={changeLoading}
                color="secondary"
                isLo
                onClick={handleSubmitPassword}
              >
                {t('change_password')}
              </Button>
              <Button color="error" onClick={handlePasswordClear}>
                {t('clear')}
              </Button>
            </Box>
          </Card>
        </Box>
      )}
      {/* На мобильных в меню нет «Выйти» — выход доступен здесь */}
      <Card sx={{ px: 4, py: 2 }}>
        <Button
          variant="outlined"
          color="error"
          fullWidth
          startIcon={<LogoutIcon />}
          onClick={logout}
        >
          {t('logout')}
        </Button>
      </Card>
      <SnackAlert
        open={openSnackbar}
        autoHideDuration={5000}
        severity={isSuccess ? 'success' : 'error'}
        variant="filled"
        onClose={handleSnackClose}
        message={
          (editSuccess
            ? t('the_changes_have_been_saved!')
            : error?.data?.message) || t('something_went_wrong!')
        }
      />
      <SnackAlert
        open={passwordSnackBar}
        autoHideDuration={5000}
        severity={changeSuccess ? 'success' : 'error'}
        variant="filled"
        onClose={passwordCloseSnack}
        message={
          (changeSuccess
            ? t('the_password_has_been_changed')
            : changeErrorData?.data?.message) || t('something_went_wrong!')
        }
      />
    </LayoutContent>
  );
}

export default MyProfile
